import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ComponentType, forwardRef, type ReactElement, type ReactNode, type SVGProps } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Spinner } from '../spinner/spinner';
import { type ButtonVariant } from './buttonVariant';
import styles from './button.module.scss';
export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'> & {
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  download?: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  isExternal?: boolean;
  isInverted?: boolean;
  isLoading?: boolean;
  isSmall?: boolean;
  isSubmitting?: boolean;
  LinkComponent?: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement>>;
  prefix?: ReactElement;
  suffix?: ReactElement;
  target?: string;
  to?: string;
  variant?: ButtonVariant;
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((t0, ref) => {
  const $ = _c(77);
  let Icon;
  let LinkComponent;
  let children;
  let className;
  let dataTestId;
  let disabled;
  let download;
  let isInverted;
  let isSmall;
  let onClick;
  let prefix;
  let props;
  let suffix;
  let t1;
  let t2;
  let t3;
  let t4;
  let target;
  let to;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      className,
      dataTestId,
      disabled,
      download,
      Icon,
      isExternal: t1,
      isInverted,
      isLoading: t2,
      isSmall,
      isSubmitting: t3,
      LinkComponent,
      onClick,
      prefix,
      suffix,
      target,
      to,
      type: t4,
      variant,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = LinkComponent;
    $[3] = children;
    $[4] = className;
    $[5] = dataTestId;
    $[6] = disabled;
    $[7] = download;
    $[8] = isInverted;
    $[9] = isSmall;
    $[10] = onClick;
    $[11] = prefix;
    $[12] = props;
    $[13] = suffix;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = target;
    $[19] = to;
    $[20] = variant;
  } else {
    Icon = $[1];
    LinkComponent = $[2];
    children = $[3];
    className = $[4];
    dataTestId = $[5];
    disabled = $[6];
    download = $[7];
    isInverted = $[8];
    isSmall = $[9];
    onClick = $[10];
    prefix = $[11];
    props = $[12];
    suffix = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    target = $[18];
    to = $[19];
    variant = $[20];
  }
  const isExternal = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const isSubmitting = t3 === undefined ? false : t3;
  const type = t4 === undefined ? "button" : t4;
  if (isLoading) {
    let t5;
    if ($[21] !== Icon) {
      t5 = Icon && {
        circle: true,
        width: 40
      };
      $[21] = Icon;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    let t6;
    if ($[23] !== t5) {
      t6 = <Skeleton height={40} width={160} {...t5} />;
      $[23] = t5;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    return t6;
  }
  const t5 = isSubmitting || undefined;
  const t6 = disabled || isSubmitting;
  let t7;
  if ($[25] !== dataTestId || $[26] !== onClick || $[27] !== props || $[28] !== ref || $[29] !== t5 || $[30] !== t6 || $[31] !== type) {
    t7 = {
      ...props,
      "aria-busy": t5,
      "data-testid": dataTestId,
      disabled: t6,
      onClick,
      ref,
      type
    };
    $[25] = dataTestId;
    $[26] = onClick;
    $[27] = props;
    $[28] = ref;
    $[29] = t5;
    $[30] = t6;
    $[31] = type;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const sharedButtonProps = t7;
  if (Icon) {
    let t8;
    if ($[33] !== className || $[34] !== isSmall) {
      t8 = classNames(className, styles["button--icon"], {
        [styles["button--small"]]: isSmall
      });
      $[33] = className;
      $[34] = isSmall;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    let t9;
    if ($[36] !== Icon || $[37] !== isSubmitting) {
      t9 = isSubmitting ? <Spinner /> : <Icon />;
      $[36] = Icon;
      $[37] = isSubmitting;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    let t10;
    if ($[39] !== sharedButtonProps || $[40] !== t8 || $[41] !== t9) {
      t10 = <button {...sharedButtonProps} className={t8}>{t9}</button>;
      $[39] = sharedButtonProps;
      $[40] = t8;
      $[41] = t9;
      $[42] = t10;
    } else {
      t10 = $[42];
    }
    return t10;
  }
  let t8;
  if ($[43] !== isSubmitting || $[44] !== prefix) {
    t8 = isSubmitting ? <Spinner /> : prefix;
    $[43] = isSubmitting;
    $[44] = prefix;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  const resolvedPrefix = t8;
  const t9 = styles[`${"button"}--${variant}`];
  const t10 = resolvedPrefix !== undefined;
  const t11 = suffix !== undefined;
  let t12;
  if ($[46] !== className || $[47] !== isInverted || $[48] !== isSmall || $[49] !== t10 || $[50] !== t11 || $[51] !== t9 || $[52] !== variant) {
    t12 = classNames([styles.button, {
      [t9]: variant,
      [styles["button--has-prefix"]]: t10,
      [styles["button--has-suffix"]]: t11,
      [styles["button--inverted"]]: isInverted,
      [styles["button--small"]]: isSmall
    }, className]);
    $[46] = className;
    $[47] = isInverted;
    $[48] = isSmall;
    $[49] = t10;
    $[50] = t11;
    $[51] = t9;
    $[52] = variant;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  const buttonClasses = t12;
  let t13;
  if ($[54] !== resolvedPrefix) {
    t13 = resolvedPrefix && <div className={styles.button__prefix}>{resolvedPrefix}</div>;
    $[54] = resolvedPrefix;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== suffix) {
    t14 = suffix && <div className={styles.button__suffix}>{suffix}</div>;
    $[56] = suffix;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  let t15;
  if ($[58] !== children || $[59] !== t13 || $[60] !== t14) {
    t15 = <>{t13}{children}{t14}</>;
    $[58] = children;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  const content = t15;
  const t16 = props as AnchorHTMLAttributes<HTMLAnchorElement>;
  let t17;
  if ($[62] !== buttonClasses || $[63] !== dataTestId || $[64] !== download || $[65] !== t16 || $[66] !== target || $[67] !== to) {
    t17 = {
      ...t16,
      className: buttonClasses,
      "data-testid": dataTestId,
      download,
      href: to,
      target
    };
    $[62] = buttonClasses;
    $[63] = dataTestId;
    $[64] = download;
    $[65] = t16;
    $[66] = target;
    $[67] = to;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  const sharedLinkProps = t17;
  let t18;
  if ($[69] !== LinkComponent || $[70] !== buttonClasses || $[71] !== content || $[72] !== isExternal || $[73] !== sharedButtonProps || $[74] !== sharedLinkProps || $[75] !== to) {
    t18 = isExternal ? <a {...sharedLinkProps}>{content}</a> : to && LinkComponent !== undefined ? <LinkComponent {...sharedLinkProps}>{content}</LinkComponent> : <button {...sharedButtonProps} className={buttonClasses}>{content}</button>;
    $[69] = LinkComponent;
    $[70] = buttonClasses;
    $[71] = content;
    $[72] = isExternal;
    $[73] = sharedButtonProps;
    $[74] = sharedLinkProps;
    $[75] = to;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  return t18;
});